import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  AcademyPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  TwoColumn,
  ImageBlock,
  ProgramsGrid,
  ProgramCard,
  ProgramImageWrapper,
  ProgramTitle,
  ProgramDescription,
  FacilitiesGrid,
  FacilityCard,
  FacilityTitle,
  FacilityDescription,
} from '../styles/AcademyPageStyles';

const CACHE_KEY = 'academy_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const AcademyPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/academy');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load academy page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <AcademyPageContainer>
        <LoadingSpinner />
      </AcademyPageContainer>
    );
  }

  if (error && !pageData) {
    return <AcademyPageContainer>{error}</AcademyPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const overviewText = pageData ? pageData.overviewText : null;
  const establishmentStory = pageData ? pageData.establishmentStory : null;
  const historyText = pageData ? pageData.historyText : null;
  const missionItems =
    pageData && Array.isArray(pageData.missionItems) ? pageData.missionItems : [];
  const activityItems =
    pageData && Array.isArray(pageData.activityItems) ? pageData.activityItems : [];
  const programs = pageData && Array.isArray(pageData.programs) ? pageData.programs : [];
  const facilities =
    pageData && Array.isArray(pageData.facilitiesGallery) ? pageData.facilitiesGallery : [];
  const enrollmentInfo = pageData ? pageData.enrollmentInfo : null;
  const contactInfo = pageData ? pageData.contactInfo : null;

  const sortedFacilities = [...facilities].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
    return ao - bo;
  });

  return (
    <AcademyPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {overviewText && (
        <Section>
          <SectionTitle>Overview</SectionTitle>
          <TextBlock>{overviewText}</TextBlock>
        </Section>
      )}

      {establishmentStory && (establishmentStory.text || establishmentStory.image) && (
        <Section>
          <SectionTitle>Establishment Story</SectionTitle>
          <TwoColumn>
            {establishmentStory.text && <TextBlock>{establishmentStory.text}</TextBlock>}
            {establishmentStory.image && (
              <ImageBlock>
                <Base64Image
                  image={establishmentStory.image}
                  alt="Establishment story"
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </ImageBlock>
            )}
          </TwoColumn>
        </Section>
      )}

      {historyText && (
        <Section>
          <SectionTitle>History</SectionTitle>
          <TextBlock>{historyText}</TextBlock>
        </Section>
      )}

      {missionItems.length > 0 && (
        <Section>
          <SectionTitle>Our Mission</SectionTitle>
          <TextBlock>
            {missionItems.map((item, index) => (
              <div key={item._id || index} style={{ marginBottom: 6 }}>
                {item && item.text}
              </div>
            ))}
          </TextBlock>
        </Section>
      )}

      {activityItems.length > 0 && (
        <Section>
          <SectionTitle>Activities</SectionTitle>
          <TextBlock>
            {activityItems.map((item, index) => (
              <div key={item._id || index} style={{ marginBottom: 6 }}>
                {item && item.text}
              </div>
            ))}
          </TextBlock>
        </Section>
      )}

      {programs.length > 0 && (
        <Section>
          <SectionTitle>Programs</SectionTitle>
          <ProgramsGrid>
            {programs.map((program, index) => (
              <ProgramCard key={program._id || program.title || index}>
                {program.image && (
                  <ProgramImageWrapper>
                    <Base64Image
                      image={program.image}
                      alt={program.title || 'Program'}
                      style={{ width: '100%', borderRadius: 6, marginBottom: 4 }}
                    />
                  </ProgramImageWrapper>
                )}
                {program.title && <ProgramTitle>{program.title}</ProgramTitle>}
                {program.description && (
                  <ProgramDescription>{program.description}</ProgramDescription>
                )}
              </ProgramCard>
            ))}
          </ProgramsGrid>
        </Section>
      )}

      {sortedFacilities.length > 0 && (
        <Section>
          <SectionTitle>School Gallery</SectionTitle>
          <FacilitiesGrid>
            {sortedFacilities.map((item, index) => (
              <FacilityCard
                key={item._id || item.title || index}
              >
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.title || 'Facility'}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                {item.title && <FacilityTitle>{item.title}</FacilityTitle>}
                {item.description && (
                  <FacilityDescription>{item.description}</FacilityDescription>
                )}
              </FacilityCard>
            ))}
          </FacilitiesGrid>
        </Section>
      )}

      {enrollmentInfo && enrollmentInfo.text && (
        <Section>
          <SectionTitle>Enrollment Information</SectionTitle>
          <TextBlock>{enrollmentInfo.text}</TextBlock>
        </Section>
      )}

      {contactInfo && (contactInfo.schoolAddress || contactInfo.contactNumbers || contactInfo.email || contactInfo.otherDetails) && (
        <Section>
          <SectionTitle>School Contact Information</SectionTitle>
          <TextBlock>
            {contactInfo.schoolAddress && (
              <div style={{ marginBottom: 8 }}>
                <strong>School Address:</strong>
                {' '}
                {contactInfo.schoolAddress}
              </div>
            )}
            {contactInfo.contactNumbers && (
              <div style={{ marginBottom: 8 }}>
                <strong>Contact Numbers:</strong>
                {' '}
                {contactInfo.contactNumbers}
              </div>
            )}
            {contactInfo.email && (
              <div style={{ marginBottom: 8 }}>
                <strong>Email:</strong>
                {' '}
                {contactInfo.email}
              </div>
            )}
            {contactInfo.otherDetails && (
              <div style={{ marginBottom: 8 }}>
                <strong>Other Details:</strong>
                {' '}
                {contactInfo.otherDetails}
              </div>
            )}
          </TextBlock>
        </Section>
      )}
    </AcademyPageContainer>
  );
};

export default AcademyPage;
